import { apiRequest } from '@/domains/api';
import type {
  IMailBody,
  IMailDeliveryRow,
  IMailDraft,
  IMailPlaceholderCatalogue,
  IMailTemplateCreate,
  IMailPreview,
  IMailSample,
  IMailTagCount,
  IMailTemplate,
  IMailTemplateSummary,
  IMailTestSent,
  IMailVariant,
  IMailVersion,
  MailPreviewRole,
  MailSurvey,
  MailTemplatePatch,
  MailUsage
} from './mail-template-contracts';

/**
 * survey-api's mail templates (SR-523), every route `mail.templates`. `survey` picks a survey
 * variant of a survey-scoped template (absent: the base). A draft's save quotes its revision in
 * `If-Match` ("0" for none), a publication the variant's latest version number; a stale one is 409
 * `revision-conflict`. A body survey-api refuses is 400 `invalid-body`, its `problems[]` targeting
 * the part (`parts.text`) or the setting (`settings.replyTo`).
 */

const MAIL_TEMPLATES = '/api/v1/admin/mail-templates';

function templatePath(code: string): string {
  return `${MAIL_TEMPLATES}/${encodeURIComponent(code)}`;
}

function surveyQuery(survey: Nullable<MailSurvey>, query = new URLSearchParams()): URLSearchParams {
  if (survey) query.set('survey', survey);
  return query;
}

function withQuery(path: string, query: URLSearchParams): string {
  const text = query.toString();
  return text ? `${path}?${text}` : path;
}

/** How a preview or a test is rendered: the variant, whose copy, the PDF, every value empty (so
 *  the fallbacks show), the values of a submitted response, and the conditions turned on — null
 *  derives them from the role and the PDF. */
export interface IMailRenderOptions {
  survey: Nullable<MailSurvey>;
  role: MailPreviewRole;
  attached: boolean;
  empty?: boolean;
  responseId?: Nullable<string>;
  /** A staff template's: the list participant whose values fill it. */
  participantId?: Nullable<string>;
  conditions?: Nullable<readonly string[]>;
}

function renderQuery(options: IMailRenderOptions): URLSearchParams {
  const query = surveyQuery(options.survey);
  query.set('role', options.role);
  query.set('attached', String(options.attached));
  if (options.empty) query.set('empty', 'true');
  if (options.responseId) query.set('responseId', options.responseId);
  if (options.participantId) query.set('participantId', options.participantId);
  if (options.conditions) query.set('conditions', options.conditions.join(','));
  return query;
}

/** A quoted revision for `If-Match`. */
const quoted = (revision: number) => `"${revision}"`;

function versionOf(version: IMailVersion): IMailVersion {
  return { ...version, note: version.note ?? '', problems: version.problems ?? [] };
}

function variantOf(variant: IMailVariant): IMailVariant {
  return {
    ...variant,
    current: variant.current ? versionOf(variant.current) : null,
    scheduled: variant.scheduled ? versionOf(variant.scheduled) : null,
    draft: variant.draft ?? null,
    versions: (variant.versions ?? []).map(versionOf)
  };
}

function templateOf(template: IMailTemplate): IMailTemplate {
  return {
    ...template,
    parts: template.parts ?? [],
    placeholders: template.placeholders ?? [],
    conditions: template.conditions ?? [],
    tags: template.tags ?? [],
    usages: template.usages ?? [],
    systemBlocks: template.systemBlocks ?? [],
    variants: (template.variants ?? []).map(variantOf)
  };
}

export interface IMailPublish extends Partial<IMailBody> {
  /** Null: now. */
  effectiveAt: Nullable<string>;
  note: string;
}

export const mailTemplatesApi = {
  /** Every template the caller may edit; `archived` the custom templates archived instead. */
  async list(
    signal?: AbortSignal,
    filter: { group?: string; usage?: MailUsage; archived?: boolean; tag?: string } = {}
  ): Promise<IMailTemplateSummary[]> {
    const query = new URLSearchParams();
    if (filter.group) query.set('group', filter.group);
    if (filter.usage) query.set('usage', filter.usage);
    if (filter.archived) query.set('archived', 'true');
    if (filter.tag) query.set('tag', filter.tag);
    const answer = await apiRequest<{ templates?: IMailTemplateSummary[] }>(withQuery(MAIL_TEMPLATES, query), {
      signal
    });
    return (answer.templates ?? []).map((entry) => ({
      ...entry,
      tags: entry.tags ?? [],
      usages: entry.usages ?? [],
      variants: entry.variants ?? []
    }));
  },

  /** The preset tags and every tag in use, presets first, then by use. */
  async tags(signal?: AbortSignal): Promise<IMailTagCount[]> {
    const answer = await apiRequest<{ tags?: IMailTagCount[] }>(`${MAIL_TEMPLATES}/tags`, { signal });
    return answer.tags ?? [];
  },

  /** A custom template (only Lists: `mail.outreach.author` or `mail.templates`; with reminders:
   *  `mail.templates`): its code made from the name, its body empty until a version is published. */
  async create(template: IMailTemplateCreate): Promise<IMailTemplate> {
    return templateOf(await apiRequest<IMailTemplate>(MAIL_TEMPLATES, { method: 'POST', body: template }));
  },

  /** A custom template's name, purpose, tags or usages — a built-in one's tags alone; the code
   *  never changes. 409 when a usage goes that a campaign or a reminder step still needs. */
  async patch(code: string, patch: MailTemplatePatch): Promise<IMailTemplate> {
    return templateOf(await apiRequest<IMailTemplate>(templatePath(code), { method: 'PATCH', body: patch }));
  },

  /** Out of the pickers; 409 `{steps}` while a reminder step or an unsent campaign uses it. */
  archive(code: string): Promise<unknown> {
    return apiRequest<unknown>(`${templatePath(code)}/archive`, { method: 'POST' });
  },

  restore(code: string): Promise<unknown> {
    return apiRequest<unknown>(`${templatePath(code)}/restore`, { method: 'POST' });
  },

  /** What a variant's body may use: the email's own placeholders, then — where it knows a
   *  participant and a survey — the field catalogue of its survey (the base: what the four share),
   *  the conditions and the lists. */
  async placeholders(code: string, survey: Nullable<MailSurvey>, signal?: AbortSignal): Promise<IMailPlaceholderCatalogue> {
    const answer = await apiRequest<Partial<IMailPlaceholderCatalogue>>(
      withQuery(`${templatePath(code)}/placeholders`, surveyQuery(survey)),
      { signal }
    );
    return {
      placeholders: (answer.placeholders ?? []).map((entry) => ({
        ...entry,
        // The mail's own placeholders are labelled by their description, a sentence: a pill says
        // their name's words instead.
        label:
          !entry.field && (entry.own || entry.group === 'Mail' || entry.group === 'This email') ? undefined : entry.label,
        description: entry.description ?? entry.label ?? entry.name,
        sample: entry.sample ?? ''
      })),
      conditions: (answer.conditions ?? []).map((entry) => ({
        ...entry,
        description: entry.description ?? entry.name,
        sample: entry.sample ?? true
      })),
      lists: answer.lists ?? []
    };
  },

  /** The template's sends counted per survey variant and version. */
  async delivery(code: string, signal?: AbortSignal): Promise<IMailDeliveryRow[]> {
    const answer = await apiRequest<{ rows?: IMailDeliveryRow[] }>(`${templatePath(code)}/delivery`, { signal });
    return answer.rows ?? [];
  },

  async get(code: string, signal?: AbortSignal): Promise<IMailTemplate> {
    return templateOf(await apiRequest<IMailTemplate>(templatePath(code), { signal }));
  },

  async preview(code: string, body: IMailBody, options: IMailRenderOptions, signal?: AbortSignal): Promise<IMailPreview> {
    const answer = await apiRequest<IMailPreview>(withQuery(`${templatePath(code)}/preview`, renderQuery(options)), {
      method: 'POST',
      body,
      signal
    });
    return { ...answer, preheader: answer.preheader ?? '' };
  },

  /** The 20 latest submitted responses whose participant matches `q` (`responses.browse`). */
  async samples(code: string, q: string, signal?: AbortSignal): Promise<IMailSample[]> {
    const query = new URLSearchParams({ q });
    const answer = await apiRequest<{ samples?: IMailSample[] }>(`${templatePath(code)}/samples?${query}`, {
      signal
    });
    return answer.samples ?? [];
  },

  /** Rendered as the preview, sent to the caller's own address; 429 `retryAfter` past ten an hour. */
  test(code: string, body: IMailBody, options: IMailRenderOptions): Promise<IMailTestSent> {
    return apiRequest<IMailTestSent>(withQuery(`${templatePath(code)}/test`, renderQuery(options)), {
      method: 'POST',
      body
    });
  },

  /** Saves the editor's state as the variant's draft — no effect on any mail. */
  async saveDraft(
    code: string,
    survey: Nullable<MailSurvey>,
    revision: number,
    draft: IMailBody & { note: string }
  ): Promise<IMailDraft> {
    const answer = await apiRequest<{ draft: IMailDraft }>(withQuery(`${templatePath(code)}/draft`, surveyQuery(survey)), {
      method: 'PUT',
      body: draft,
      headers: { 'If-Match': quoted(revision) }
    });
    return answer.draft;
  },

  discardDraft(code: string, survey: Nullable<MailSurvey>, revision: number): Promise<void> {
    return apiRequest<void>(withQuery(`${templatePath(code)}/draft`, surveyQuery(survey)), {
      method: 'DELETE',
      headers: { 'If-Match': quoted(revision) }
    });
  },

  /** The body (or, without parts, the draft) as the variant's next version, from `effectiveAt`.
   *  `latest` is the variant's highest version number, 0 for none. */
  publish(code: string, survey: Nullable<MailSurvey>, latest: number, publication: IMailPublish): Promise<unknown> {
    return apiRequest<unknown>(withQuery(`${templatePath(code)}/publish`, surveyQuery(survey)), {
      method: 'POST',
      body: publication,
      headers: { 'If-Match': quoted(latest) }
    });
  },

  /** A scheduled version never takes effect. */
  withdraw(code: string, survey: Nullable<MailSurvey>, version: number): Promise<unknown> {
    return apiRequest<unknown>(
      withQuery(`${templatePath(code)}/versions/${version}/withdraw`, surveyQuery(survey)),
      { method: 'POST' }
    );
  },

  /** The survey's mails go back to the base text: its current and scheduled versions withdrawn. */
  removeVariant(code: string, survey: MailSurvey): Promise<unknown> {
    return apiRequest<unknown>(`${templatePath(code)}/variants/${encodeURIComponent(survey)}`, { method: 'DELETE' });
  }
};
